import React, { useEffect, useState, useRef } from "react";
import { Typography, Button, Tabs, Tab } from "@mhome/ui";
import { useRecoilValue } from "recoil";
import LoadingSpinner from "../common/loading";
import { wsReadyState } from "../global-state";
import {
  getIntegration,
  installIntegration,
  syncEntities,
} from "../service/integration-service";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";
import { useHistory, useLocation } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SyncIcon from "@mui/icons-material/Sync";
import GetAppIcon from "@mui/icons-material/GetApp";
import EntityListView from "./entity-list-view";
import DeviceListView from "./device-list-view";
import GroupListView from "./group-list-view";
import ProviderListView from "./provider-list-view";
import InfoContentView from "./info-content-view";
import RoomManageView from "./room-manage-view";

const IntegrationDetailView = ({
  showFilter = false,
  setShowFilter,
  isChatSidebarVisible = false,
}) => {
  const { sendAndWaitWs } = useWebSocket();
  const wsReady = useRecoilValue(wsReadyState);
  const { successToast, errorToast } = useToast();
  const [integrationData, setIntegrationData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const integrationId = searchParams.get("id");
  const tabsRef = useRef(null);

  const handleLoadIntegration = async () => {
    setLoading(true);
    try {
      const resp = await getIntegration(sendAndWaitWs, integrationId);
      if (resp) {
        setIntegrationData(resp);
        console.debug("integration", resp);
        console.debug("integration installed", resp.installed);
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast("Error!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (wsReady) {
      handleLoadIntegration();
    }
  }, [integrationId, wsReady]);

  const handleGroupChanged = (groups) => {
    setIntegrationData((prev) => ({ ...prev, groups }));
  };

  const getAvailableTabs = () => {
    if (!integrationData) return [];

    const tabs = [];

    // Check each type in meta.entityEnabled
    if (integrationData.meta?.entityEnabled) {
      Object.entries(integrationData.meta.entityEnabled).forEach(
        ([type, enabled]) => {
          if (enabled) {
            const typeName = integrationData.meta.typeNames[type];
            if (typeName) {
              // Use DeviceListView for basic.device integration, otherwise use EntityListView
              const isDeviceIntegration =
                integrationData.integration === "basic.device";
              tabs.push({
                hash: "entity",
                label: typeName,
                content: isDeviceIntegration ? (
                  <DeviceListView
                    meta={integrationData.meta}
                    entities={integrationData.entities[type] || []}
                    showFilter={showFilter}
                    isChatSidebarVisible={isChatSidebarVisible}
                  />
                ) : (
                  <EntityListView
                    meta={integrationData.meta}
                    entities={integrationData.entities[type] || []}
                    showFilter={showFilter}
                  />
                ),
              });
            }
          }
        }
      );
    }

    // Check each type in meta.groupEnabled
    if (integrationData.meta?.groupEnabled) {
      Object.entries(integrationData.meta.groupEnabled).forEach(
        ([type, enabled]) => {
          if (enabled) {
            const typeName = integrationData.meta.typeNames[type];
            if (typeName) {
              tabs.push({
                hash: "group",
                label: `${typeName} Group`,
                content: (
                  <GroupListView
                    onGroupChanged={handleGroupChanged}
                    groupData={integrationData.groups[type] || []}
                    entityData={integrationData.entities[type] || []}
                    entityType={type}
                    showFilter={showFilter}
                  />
                ),
              });
            }
          }
        }
      );
    }

    // Add Room tab for device integration before provider
    if (
      integrationData.integration === "basic.device" &&
      integrationData.installed
    ) {
      tabs.push({
        hash: "room",
        label: "Room",
        content: <RoomManageView />,
      });
    }

    if (integrationData.meta?.providerEnabled) {
      tabs.push({
        hash: "provider",
        label: "Provider",
        content: (
          <ProviderListView
            providers={integrationData.providers}
            showFilter={showFilter}
            onLoadIntegration={handleLoadIntegration}
          />
        ),
      });
    }

    tabs.push({
      hash: "info",
      label: "Info",
      content: (
        <InfoContentView
          integrationData={integrationData}
          location={location}
          history={history}
        />
      ),
    });

    return tabs;
  };

  const goTab = (newHash) => {
    const searchParams = new URLSearchParams(location.search);
    const searchString = searchParams.toString();
    let newUrl = location.pathname;
    if (searchString) {
      newUrl += `?${searchString}`;
    }
    if (newHash) {
      newUrl += `#${newHash}`;
    }
    history.replace(newUrl);
  };

  const scrollTabToCenter = (tabIndex) => {
    if (tabsRef.current) {
      const tabsContainer = tabsRef.current.querySelector(".MuiTabs-scroller");
      const tabElements = tabsRef.current.querySelectorAll(".MuiTab-root");

      if (tabsContainer && tabElements[tabIndex]) {
        const selectedTab = tabElements[tabIndex];
        const containerWidth = tabsContainer.clientWidth;
        const tabLeft = selectedTab.offsetLeft;
        const tabWidth = selectedTab.offsetWidth;

        // Calculate the scroll position to center the tab
        const scrollLeft = tabLeft - (containerWidth - tabWidth) / 2;

        tabsContainer.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    const tabs = getAvailableTabs();
    const newHash = tabs[newValue].hash || "";
    goTab(newHash);
    setTabIndex(newValue);

    // Scroll the selected tab to center
    setTimeout(() => scrollTabToCenter(newValue), 100);
  };

  useEffect(() => {
    if (!integrationData) return;

    const tabs = getAvailableTabs();
    if (!integrationData.installed) {
      goTab("info");
    }

    const hash = location.hash.replace("#", "");
    let newIndex = tabs.findIndex((tab) => tab.hash === hash);
    if (newIndex === -1) newIndex = 0;
    setTabIndex(newIndex);

    // Scroll to center the tab after setting it
    setTimeout(() => scrollTabToCenter(newIndex), 100);
  }, [integrationData]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    let newIndex = tabs.findIndex((tab) => tab.hash === hash);
    if (newIndex === -1) newIndex = 0;
    setTabIndex(newIndex);

    // Scroll to center the tab after setting it
    setTimeout(() => scrollTabToCenter(newIndex), 100);
  }, [location.hash]);

  const handleInstall = async (integration) => {
    const resp = await installIntegration(sendAndWaitWs, integration);
    if (resp) {
      successToast("Installed");
      handleLoadIntegration();
    } else {
      errorToast("Error!");
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    const response = await syncEntities(sendAndWaitWs, integrationId);
    if (response) {
      successToast("Sync successful");
      handleLoadIntegration(); // Reload integration data after sync
    } else {
      errorToast("Error!");
    }
    setIsSyncing(false);
  };

  const tabs = getAvailableTabs();

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        integrationData && (
          <div
            style={{
              position: "relative",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: 1,
                overflow: "auto",
                paddingLeft: "16px",
                paddingRight: "16px",
                paddingTop: "16px",
              }}
            >
              {/* Header Section */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                <Typography variant="h4">{integrationData.name}</Typography>
                {integrationData.installed && integrationData.entityEnabled && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SyncIcon />}
                    onClick={handleSync}
                    disabled={isSyncing}
                    style={{
                      height: "32px",
                      textTransform: "none",
                      boxShadow: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    Sync
                  </Button>
                )}
                {!integrationData?.meta?.providerEnabled &&
                  !integrationData.installed &&
                  integrationData.integration !== "basic.time" && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<GetAppIcon />}
                      onClick={() => handleInstall(integrationData.integration)}
                      style={{
                        height: "32px",
                        textTransform: "none",
                        fontWeight: 600,
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        background: `linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.8))`,
                        transition: "all 0.2s ease-in-out",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0,0,0,0.2)";
                        e.currentTarget.style.background = `linear-gradient(135deg, hsl(var(--primary) / 0.8), hsl(var(--primary)))`;
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.15)";
                        e.currentTarget.style.background = `linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.8))`;
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.transform = "translateY(0px)";
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                    >
                      Install
                    </Button>
                  )}
              </div>

              {/* Organization and Status Section */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  marginBottom: "8px",
                }}
              >
                <Typography
                  variant="subtitle1"
                  className="text-muted-foreground"
                >
                  {integrationData.org}
                </Typography>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    borderRadius: "16px",
                    backgroundColor: integrationData.installed
                      ? "rgba(76, 175, 80, 0.2)"
                      : "rgba(255, 215, 0, 0.2)",
                    border: `1px solid ${
                      integrationData.installed
                        ? "rgba(76, 175, 80, 0.4)"
                        : "rgba(255, 215, 0, 0.4)"
                    }`,
                  }}
                >
                  {integrationData.installed ? (
                    <CheckCircleOutlineIcon
                      style={{
                        fontSize: 16,
                        color: "#4caf50",
                      }}
                    />
                  ) : (
                    <WarningAmberIcon
                      style={{
                        fontSize: 16,
                        color: "#FFD700",
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    style={{
                      fontWeight: 500,
                      color: integrationData.installed ? "#4caf50" : "#FFD700",
                    }}
                  >
                    {integrationData.installed ? "Installed" : "Not Installed"}
                  </Typography>
                </div>
              </div>

              {/* Tabs Section */}
              <div
                className="w-full"
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <style>
                  {`
                    .integration-tabs-wrapper::-webkit-scrollbar {
                      display: none;
                    }
                    .integration-tabs-container {
                      min-width: max-content !important;
                    }
                    .integration-tabs-container > div:first-child {
                      gap: 6px !important;
                      padding-left: 0 !important;
                      padding-right: 0 !important;
                      padding-top: 8px !important;
                      padding-bottom: 8px !important;
                      flex-wrap: nowrap !important;
                    }
                    .integration-tab-button {
                      min-height: 32px !important;
                      min-width: 60px !important;
                      padding: 6px 10px !important;
                      margin-left: 3px !important;
                      margin-right: 3px !important;
                      font-size: 12px !important;
                      flex-shrink: 0 !important;
                      white-space: nowrap !important;
                    }
                  `}
                </style>
                <div
                  className="integration-tabs-wrapper"
                  style={{ width: "100%", overflowX: "auto" }}
                >
                  <Tabs
                    ref={tabsRef}
                    value={tabIndex}
                    onChange={handleTabChange}
                    aria-label="integration tabs"
                    className="integration-tabs-container"
                    style={{
                      minHeight: "auto",
                      width: "max-content",
                      minWidth: "100%",
                    }}
                  >
                    {tabs.map((tab, index) => (
                      <Tab
                        key={index}
                        label={tab.label}
                        className="integration-tab-button"
                        style={{
                          minHeight: "32px",
                          minWidth: "60px",
                          padding: "6px 10px",
                          marginLeft: "3px",
                          marginRight: "3px",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                      />
                    ))}
                  </Tabs>
                </div>
              </div>

              <div
                style={{
                  overflow: "auto",
                }}
              >
                {tabs[tabIndex]?.content}
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default IntegrationDetailView;
